import ButtonV2 from "components/elements/buttonV2"
import { navigate } from "gatsby"
import React, { useEffect, useRef, useState } from "react"
import "../../elements/placesAutocomplete/placesAutocomplete.scss"
import "./search-input.scss"

// Extend the Window interface to include Google Maps
declare global {
  interface Window {
    google: any
  }
}

interface SearchInputProps {
  defaultValue?: string
}

const SearchInput = ({ defaultValue = "" }: SearchInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const autocompleteRef = useRef<any>(null)
  const listenerRef = useRef<any>(null)
  const [inputValue, setInputValue] = useState(defaultValue)

  const loadGoogleMapsScript = () => {
    return new Promise<void>((resolve) => {
      // Check if Google Maps is already loaded
      if (window.google?.maps?.places) {
        resolve()
        return
      }

      const apiKey = process.env.GATSBY_G_CLOUD_API
      if (!apiKey || apiKey === 'YOUR_API_KEY_HERE') {
        resolve()
        return
      }

      // Check if script is already being loaded
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]')
      if (existingScript) {
        existingScript.addEventListener('load', resolve)
        return
      }

      // Create and load the Google Maps script
      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?libraries=places&key=${apiKey}`
      script.async = true
      script.defer = true
      script.onload = () => resolve()
      document.head.appendChild(script)
    })
  }

  const initializeAutocomplete = () => {
    if (inputRef.current && window.google?.maps?.places) {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          componentRestrictions: { country: "us" },
          types: ["geocode", "establishment"],
        }
      )

      listenerRef.current = autocompleteRef.current.addListener("place_changed", () => {
        const place = autocompleteRef.current?.getPlace()
        if (place?.formatted_address) {
          // Update the input value with the selected place, but don't navigate automatically
          setInputValue(place.formatted_address)
        }
      })
    }
  }

  useEffect(() => {
    loadGoogleMapsScript().then(() => {
      setTimeout(initializeAutocomplete, 100)
    })

    return () => {
      if (listenerRef.current && window.google?.maps) {
        window.google.maps.event.removeListener(listenerRef.current)
      }
    }
  }, [])

  const handleSubmit = () => {
    if (inputValue.trim()) {
      navigate(`/locations?address=${encodeURIComponent(inputValue.trim())}`)
    }
  }

  const apiKey = process.env.GATSBY_G_CLOUD_API
  const hasValidApiKey = apiKey && apiKey !== 'YOUR_API_KEY_HERE'

  return (
    <>
      <div className="google-places-autocomplete-wrapper">
        <input
          name="location-search"
          type="text"
          ref={inputRef}
          className="auto-complete"
          placeholder={
            hasValidApiKey
              ? "Zip Code"
              : "Google Maps API key not configured"
          }
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          disabled={!hasValidApiKey}
        />
        <button
          className="search-icon-button"
          disabled={!inputValue.trim() || !hasValidApiKey}
          onClick={handleSubmit}
          type="button"
        >
          <svg
            className="search-icon"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-label="Search"
          >
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
        </button>

      </div>
      <div className="search-input-button">
        <ButtonV2
          className="btn-v2--primary-green btn-v2--small"
          href=""
          id=""
          onClick={handleSubmit}
          showArrow={true}
          text="Search"
          disabled={!inputValue.trim() || !hasValidApiKey}
        />
      </div>
    </>
  )
}

export default SearchInput